import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, PenLine } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, SectionHeading } from "./Reveal";

export const readMinutes = (content = "") =>
  Math.max(1, Math.round(content.split(/\s+/).filter(Boolean).length / 200));

export const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

const Blogs = () => {
  const [posts, setPosts] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/blogs").then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);

  return (
    <section id="blog" data-testid="blog-journal-section" className="py-20 sm:py-28 bg-white/60 border-y border-[#0D3B3E]/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeading number="06" title="From the Desk · Journal" testId="blog-journal-heading" />

        {posts === null && (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-56 bg-[#0D3B3E]/[0.04] animate-pulse border border-[#0D3B3E]/10" />
            ))}
          </div>
        )}

        {posts !== null && posts.length === 0 && (
          <Reveal>
            <div
              data-testid="blog-empty-state"
              className="border border-dashed border-[#0D9488]/50 bg-white p-10 sm:p-14 text-center"
            >
              <PenLine className="w-6 h-6 text-[#0D9488] mx-auto mb-4" />
              <p className="font-display font-semibold text-xl text-[#0D3B3E]">The first article is being written</p>
              <p className="mt-2 text-sm text-slate-600">
                Notes on gentle healing, nutrition and everyday immunity — arriving soon.
              </p>
            </div>
          </Reveal>
        )}

        {posts !== null && posts.length > 0 && (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
            {posts.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.07}>
                <article
                  data-testid={`blog-card-${i}`}
                  onClick={() => navigate(`/blog/${p.slug}`)}
                  className="group h-full bg-white border border-[#0D3B3E]/10 p-7 flex flex-col cursor-pointer transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1.5 hover:border-[#0D9488]/50 hover:shadow-[0_20px_50px_rgba(13,59,62,0.08)]"
                >
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-[9px] font-mono tracking-[0.2em] text-[#0D9488] border border-[#0D9488]/30 rounded-full px-2.5 py-1">
                      {p.tag}
                    </span>
                    <span className="text-[10px] font-mono tracking-[0.15em] text-slate-400">
                      {formatDate(p.created_at)}
                    </span>
                  </div>
                  <h3 className="font-display font-semibold text-xl sm:text-2xl text-[#0D3B3E] leading-snug tracking-tight">
                    {p.title}
                  </h3>
                  {p.excerpt && (
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{p.excerpt}</p>
                  )}
                  <p className="mt-6 pt-4 border-t border-[#0D3B3E]/10 flex items-center justify-between text-[10px] font-mono tracking-[0.2em] text-slate-500">
                    {readMinutes(p.content)} MIN READ
                    <span className="flex items-center gap-1.5 text-[#0D9488] group-hover:gap-2.5 transition-[gap] duration-300">
                      READ <ArrowUpRight className="w-3.5 h-3.5" />
                    </span>
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Blogs;
